import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, createSlots as _createSlots, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "container"
}
const _hoisted_2 = {
  key: 1,
  class: "grid-loading-pane"
}

import { PropType, ref, watch, computed, useSlots } from "vue";
import DxDataGrid, {
  DxFilterRow,
  DxScrolling,
  DxSearchPanel,
  DxToolbar,
  DxItem,
  DxRowDragging,
} from "devextreme-vue/data-grid";
import DxLoadIndicator from "devextreme-vue/load-indicator";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver-es";
import { KeyDownEvent, Column } from "devextreme/ui/data_grid";


export default /*#__PURE__*/_defineComponent({
  __name: 'grid-base',
  props: {
  /**
   * An array of data items to be displayed in the data grid.
   * @type {any[]}
   * @required false
   */
  dataSource: {
    type: Array as PropType<any[]>,
    required: false,
  },
  /**
   * The name of the column that contains unique identifiers.
   * @type {string}
   * @default 'id'
   */
  idColumnName: {
    type: String,
    default: "id",
  },
  /**
   * The ID of the pre-selected item.
   * @type {number}
   * @default -1
   */
  preSelectedId: {
    type: Number,
    default: -1,
  },
  /**
   * Determines whether the component status is loading.
   * @type {boolean}
   * @default false
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * An array of custom slot names for columns.
   * @type {string[]}
   */
  customColumnSlots: {
    type: Array as PropType<string[]>,
  },
  /**
   * The name of the data grid component.
   * @type {string}
   * @default 'DataGrid'
   */
  name: {
    type: String,
    default: "DataGrid",
  },
  /**
   * Determines whether to run export functionality for the grid.
   * @type {boolean}
   * @default true
   */
  runExport: {
    type: Boolean,
    default: true,
  },
  /**
   * Determines whether word wrap is enabled in the grid cells.
   * @type {boolean}
   * @default false
   */
  wordWrapEnabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show filters for the columns.
   * @type {boolean}
   * @default false
   */
  showColumnFilters: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show the search panel.
   * @type {boolean}
   * @default true
   */
  showSearchPanel: {
    type: Boolean,
    default: true,
  },
  /**
   * Determines whether to show column headers.
   * @type {boolean}
   * @default true
   */
  showColumnHeaders: {
    type: Boolean,
    default: true,
  },
  /**
   * The text displayed when there are no records found.
   * @type {string}
   * @default 'No records found'
   */
  noDataText: {
    type: String,
    default: "No records found",
  },
  /**
   * Determines whether key press detection is enabled.
   * @type {boolean}
   * @default false
   */
  enableKeyPressDetection: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether reordering of rows is allowed.
   * @type {boolean}
   * @default false
   */
  allowReordering: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether auto focussing on selected row is enabled
   * @type {boolean}
   * @default true
   */
  focusRowEnabled: {
    type: Boolean,
    default: true,
  },
  /**
   * The field name which will be used to disable rows from grid.
   * @type {string}
   * @default ''
   */
  restrictedFieldName: {
    type: String,
    default: "",
  },
  /**
   * Determines whether SelectPreviousRow is applied
   * @type {boolean}
   * @default true
   */
  isAllowSelectPreviousRow: {
    type: Boolean,
    default: true,
  },
},
  emits: [
  /**
   * Emits an event when a row is prepared (updates with scroll or click).
   * Passes the selected row data to the parent component.
   * @param {Object} e - The prepared row's data.
   */
  "rowPrepared",
  /**
   * Emits an event when a row is double clicked.
   * Passes the selected row data to the parent component.
   * @param {Object} e - The clicked row's data.
   */
  "rowClick",
  /**
   * Emits an event when a row is selected.
   * Sends both the row data and the row index to the parent component.
   * @param {Object} e - The selected row's  data.
   * @param {number} rowIndex - The index of the selected row.
   */
  "rowSelected",
  /**
   * Updates the state of the `runExport` prop. Part of a two-way binding (v-model:run-export).
   * Emitted to trigger the Excel export process in the parent component.
   * @param {boolean} value - The new value for the `runExport` flag.
   */
  "update:runExport",
  /**
   * Emits an event when a user finishes dragging and dropping a row to reorder it.
   * Passes the event object containing details about the drag-and-drop operation.
   * @param {Object} e - The event object from the row reordering action.
   */
  "onRowReorder",
  /**
   * Emits an event when the "show hidden information" action is clicked on an adaptive detail row.
   * Passes the data of the selected adaptive row to the parent component.
   * @param {Object} selectedRow.data - The data of the adaptive detail row.
   */
  "adaptiveRowSelection",
],
  setup(__props, { emit }) {

const props = __props;





const rowToSelectId = ref<string | number | undefined>();
const isSorting = ref(false);

const selectedIdHasTruthyValue = computed(() => {
  return (
    (typeof rowToSelectId.value == "number" && rowToSelectId.value > -1) ||
    (typeof rowToSelectId.value == "string" && rowToSelectId.value != "")
  );
});

watch(
  () => props.loading,
  () => {
    rowToSelectId.value = undefined;
  },
);

watch(
  () => rowToSelectId.value,
  async () => {
    if (selectedIdHasTruthyValue.value || !grid.value) {
      const selection = await grid.value.instance.selectRows(
        rowToSelectId.value,
        false,
      );
      selectedRow.value = selection[0];
      emit("rowSelected", selectedRow.value);
    }
  },
);

const lastClickedRowKey = ref<string | number | null>(null);

/** Handles the row double click event */
const onRowClick = (e: any) => {
  /* Due to the row double click event having unpredictable results in Chrome
   * We must instead handle the click event and check whether 2 clicks have been
   * performed in quick succession.
   * Code is taken from: https://supportcenter.devexpress.com/ticket/details/t143438/datagrid-provide-the-row-double-click-event */
  var component = e.component;
  var prevClickTime = component.lastClickTime;
  component.lastClickTime = new Date();
  const currentRowKey = e.key;

  // Check if the click was within the last 300ms
  if (
    currentRowKey === lastClickedRowKey.value &&
    prevClickTime &&
    component.lastClickTime - prevClickTime < 300
  ) {
    emit("rowClick", e.data);
  }

  lastClickedRowKey.value = currentRowKey;
};

const getCustomColumnDataSlotName = (slotName: string) => `${slotName}Data`;

const selectedRow = ref<any>();

/** Handles the row selection changed event */
const onSelectionChanged = (e: any) => {
  if (selectedIdHasTruthyValue.value) {
    return;
  }
  // Retrieve row details for action button slot including the index of the Row
  selectedRow.value = e.selectedRowsData[0];
  // As we don't want to just get the index of the visible rows on the screen
  // We should use get Global Row Index By Key to retrieve the global index of the row
  e.component
    .getController("data")
    .getGlobalRowIndexByKey(e.selectedRowKeys[0])
    .done((rowIndex: any) => emit("rowSelected", selectedRow.value, rowIndex));
};

const focusedRowId = computed(() => {
  return selectedIdHasTruthyValue.value
    ? rowToSelectId.value
    : props.preSelectedId;
});

const grid = ref<InstanceType<typeof DxDataGrid | null>>(null);

/** File name for excel export - this includes current date/time so users can download the same grid multiple times */
const excelExportFileName = () =>
  `${props.name}-${new Date().toISOString()}.xlsx`;

/* When parent component sets runExport prop to true, export the grid to excel
 * This overcomes the issue of having a menu external to the grid*/
watch(
  () => props.runExport,
  async () => {
    if (!props.runExport) {
      return;
    }

    await exportGridToExcel();
  },
);

/** Exports the data displayed in the grid to an XLSX file */
const exportGridToExcel = async () => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet("Data");

  await exportDataGrid({
    component: grid.value.instance,
    worksheet: worksheet,
    encodeExecutableContent: true,
  });

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    excelExportFileName(),
  );

  // reset v-model property so user can click the export menu item again
  runExportToExcel.value = false;
};

const runExportToExcel = computed({
  get: () => props.runExport,
  set: (value: boolean) => emit("update:runExport", value),
});

const onOptionChanged = (e: any) => {
  if (e.fullName.includes("sortOrder")) {
    isSorting.value = true;

    // Temporarily "disable" focus navigation
    if (grid.value) {
      grid.value.instance.option("focusedRowEnabled", false);
    }
  }
};

const selectPreviousRow = (e: any) => {
  // 1. If sorting, KILL all selection logic to prevent jumpy scrolling
  if (isSorting.value) {
    setTimeout(() => {
      isSorting.value = false;
    }, 300);
    return;
  }

  // 2. Only perform selection if currently not sorting and have an ID
  if (props.isAllowSelectPreviousRow && props.preSelectedId !== -1) {
    const gridInstance = e.component;

    // Check if the row is already selected to avoid redundant "jumping"
    const selectedKeys = gridInstance.getSelectedRowKeys();
    if (!selectedKeys.includes(props.preSelectedId)) {
      gridInstance.selectRows([props.preSelectedId], false);
    }
  }
};

// showHiddenInformationClick gets called when the user clicks on the ... button to reveal a row's hidden data
// The function finds the row out of all the visible rows and emits its data
const showHiddenInformationClick = (e: any) => {
  const visibleRows = grid.value.instance.getVisibleRows();
  const selectedRow = visibleRows.find((row: any) => {
    return row.rowType == "detailAdaptive";
  });
  if (selectedRow) {
    emit("adaptiveRowSelection", selectedRow.data);
  }
  e.formOptions.colCount = 1;
};

/** Determines the position of the search panel in the grid toolbar */
const searchPosition = computed(() => {
  return isHeaderContentSlotUsed() ? "before" : "after";
});

/** Checks whether the Header Content slot has been used */
const isHeaderContentSlotUsed = () => {
  const slots = useSlots();
  return slots["header-content"];
};

/** Determines whether the grid toolbar should be shown */
const showToolbar = computed(
  () => isHeaderContentSlotUsed() || props.showSearchPanel,
);

const onKeyDown = async (e: KeyDownEvent) => {
  if (
    !props.enableKeyPressDetection ||
    !props.dataSource ||
    props.dataSource.length == 0 ||
    !grid.value ||
    !e.event
  ) {
    return;
  }
  const firstVisibleColumn = getFirstVisibleTextColumn();

  if (firstVisibleColumn?.dataField == undefined) {
    return;
  }

  const foundRecord = getFirstRecordStartingWithPressedKey(
    e.event.key,
    firstVisibleColumn.dataField,
  );

  if (!foundRecord) {
    return;
  }

  rowToSelectId.value = foundRecord[props.idColumnName];
};

const getFirstVisibleTextColumn = () => {
  // We need to programatically fetch the columns then find the first column that is visible. We take that column's data field and use it
  // to find the first row that contains the specified key as it's first letter.
  const displayedColumns: Column[] = grid.value.instance.getVisibleColumns();
  return displayedColumns.find(
    (column) => column.visible && column.dataType == "string",
  );
};

const getFirstRecordStartingWithPressedKey = (
  key: string,
  columnName: string,
) => {
  return props.dataSource?.find(
    (record) => record[columnName].charAt(0).toUpperCase() == key.toUpperCase(),
  );
};

/**
 * Handles the row preparation event in the data grid.
 * Disables interaction and applies styling to rows based on specific flags.
 *
 * @param {any} e - The event object containing row data and metadata.
 */
const onRowPrepared = (e: any) => {
  if (
    e.data != undefined &&
    e.rowType === "data" &&
    props.restrictedFieldName &&
    e.data[props.restrictedFieldName]
  ) {
    e.rowElement.style.opacity = "0.5";
    e.rowElement.style.pointerEvents = "none";
    e.rowElement.style.backgroundColor = "#f5f5f5";
  }
  emit("rowPrepared", e);
};

return (_ctx: any,_cache: any) => {
  return (!__props.loading)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(DxDataGrid), {
          "row-alternation-enabled": true,
          ref_key: "grid",
          ref: grid,
          class: "dx-card wide-card",
          "data-source": __props.dataSource,
          "key-expr": __props.idColumnName,
          "focused-row-key": focusedRowId.value,
          "show-borders": false,
          "focused-row-enabled": __props.focusRowEnabled && !isSorting.value,
          "column-auto-width": false,
          "column-hiding-enabled": true,
          style: {"height":"100%"},
          selection: { mode: 'single' },
          onRowClick: onRowClick,
          onSelectionChanged: onSelectionChanged,
          hoverStateEnabled: true,
          wordWrapEnabled: __props.wordWrapEnabled,
          "repaint-changes-only": true,
          "cache-enabled": true,
          "remote-operations": false,
          onOptionChanged: onOptionChanged,
          onContentReady: selectPreviousRow,
          onAdaptiveDetailRowPreparing: showHiddenInformationClick,
          "show-column-headers": __props.showColumnHeaders,
          "no-data-text": __props.noDataText,
          onKeyDown: onKeyDown,
          onRowPrepared: onRowPrepared
        }, _createSlots({
          "header-content": _withCtx(() => [
            _renderSlot(_ctx.$slots, "header-content")
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(DxScrolling), {
              mode: "virtual",
              rowRenderingMode: "virtual",
              preloadEnabled: true
            }),
            _createVNode(_unref(DxFilterRow), { visible: __props.showColumnFilters }, null, 8, ["visible"]),
            (showToolbar.value)
              ? (_openBlock(), _createBlock(_unref(DxToolbar), { key: 0 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DxItem), {
                      location: searchPosition.value,
                      name: "searchPanel"
                    }, null, 8, ["location"]),
                    _createVNode(_unref(DxItem), {
                      location: "after",
                      template: "header-content"
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(_unref(DxRowDragging), {
              "allow-reordering": !isSorting.value && props.allowReordering,
              "show-drag-icons": false,
              "on-reorder": (e) => emit('onRowReorder', e)
            }, null, 8, ["allow-reordering", "on-reorder"]),
            _createVNode(_unref(DxSearchPanel), { visible: __props.showSearchPanel }, null, 8, ["visible"]),
            _renderSlot(_ctx.$slots, "columns"),
            _renderSlot(_ctx.$slots, "action-button", { row: selectedRow.value })
          ]),
          _: 2
        }, [
          _renderList(__props.customColumnSlots, (slot, index) => {
            return {
              name: slot,
              fn: _withCtx(({ data }) => [
                _renderSlot(_ctx.$slots, getCustomColumnDataSlotName(slot), {
                  data: data.row.data
                })
              ])
            }
          })
        ]), 1032, ["data-source", "key-expr", "focused-row-key", "focused-row-enabled", "wordWrapEnabled", "show-column-headers", "no-data-text"])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_2, [
        _createVNode(_unref(DxLoadIndicator), {
          height: 50,
          width: 50
        })
      ]))
}
}

})